import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid2 as Grid,
  List,
  ListItem,
  Pagination,
  Stack
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusCircle } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { useNotify } from '../hooks';
import { PATHS } from '../paths';
import { surfaceSx } from '../styles/tokens';
import { AccidentAccidentInProgressDialog } from './accidents/AccidentAccidentInProgressDialog';
import { AccidentItem } from './accidents/AccidentItem';
import { AccidentMerchantItem } from './accidents/AccidentMerchantItem';
import { AccidentRemoveConfirmDialog } from './accidents/AccidentRemoveConfirmDialog';
import { AccidentFiltersType, AccidentsFilters } from './accidents/AccidentsFilters';
import { FlexBlock, PageLayout, TransparentChip } from './common';
import { NoDataFound } from './common/NoDataFound';
import { useAuth } from './hooks';
import { Role } from './types';
import { Accident, AccidentStatusResponse, RegistryType } from './types/accident';

const ACCIDENTS_LIMIT = 20;

interface AccidentsListProps {
  accidents: Accident[];
  detailPath: (accidentId: string) => string;
  onRemove?: (id: number) => void;
}

const AccidentsList = ({ accidents, detailPath, onRemove }: AccidentsListProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <>
      <Grid container>
        {user?.role === Role.User ? (
          accidents?.map((accident) => {
            const accidentDetailPath = detailPath(accident.id.toString());
            return (
              <Grid key={accident.id} size={{ xs: 12, sm: 6, xl: 4 }} padding={1}>
                <AccidentItem
                  {...accident}
                  extra={
                    <>
                      <Box my={1}>
                        <Divider />
                      </Box>
                      <Button variant="outlined" color="inherit" onClick={() => navigate(accidentDetailPath)}>
                        Detail
                      </Button>
                    </>
                  }
                />
              </Grid>
            );
          })
        ) : (
          <List
            sx={{
              ...surfaceSx,
              width: 1,
              paddingX: 0,
              paddingY: 0,
              overflow: 'hidden'
            }}
          >
            {accidents?.map((accident, idx) => {
              const accidentDetailPath = detailPath(accident.id.toString());
              return (
                <React.Fragment key={accident.id}>
                  <ListItem
                    sx={{
                      paddingX: 2,
                      paddingY: 2,
                      paddingTop: !accident?.status?.progress?.isCompleted ? 5 : 2,
                      width: 1,
                      position: 'relative',
                      boxShadow: !accident?.status?.progress?.isCompleted ? 'inset 3px 0 0 #C62327' : 'none',
                      '&:hover': { backgroundColor: 'rgba(34, 34, 52, 0.025)' }
                    }}
                  >
                    {!accident?.status?.progress?.isCompleted && (
                      <TransparentChip
                        color="error"
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: 12
                        }}
                        label="Doplnit"
                        size="small"
                      />
                    )}
                    <AccidentMerchantItem
                      accident={accident}
                      onClick={() => navigate(accidentDetailPath)}
                      onRentalClick={(id) => navigate(PATHS.CAR_RENTAL(id))}
                      onRemove={() => onRemove?.(accident.id)}
                    />
                  </ListItem>
                  {idx < accidents.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Grid>
    </>
  );
};

interface AccidentsPageProps {
  accidentDetailPath: (accidentId: string) => string;
}

export const AccidentsPage = ({ accidentDetailPath }: AccidentsPageProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openNewAccidentDialog, setOpenNewAccidentDialog] = useState(false);
  const [openRemoveAccidentDialog, setOpenRemoveAccidentDialog] = useState<number | undefined>(undefined);
  const { notifyOnSuccess, notifyOnError } = useNotify();

  const page = Number(searchParams.get('page') ?? '1');
  const offset = (page - 1) * ACCIDENTS_LIMIT;

  const filters: AccidentFiltersType = {
    search: searchParams.get('search') ?? '',
    statusId: searchParams.get('statusId') ? Number(searchParams.get('statusId')) : undefined
  };

  const { data: accidentsStatuses } = useQuery<AccidentStatusResponse[]>(
    useCommonApi().getRegistryList(RegistryType.ACCIDENT_STATUSES)
  );

  const { data, isPending } = useQuery(
    useCommonApi().getAccidents({
      limit: ACCIDENTS_LIMIT,
      offset,
      search: filters.search || undefined,
      statusId: filters.statusId
    })
  );

  const accidents = data?.items ?? [];
  const totalPages = data?.pagination.pages ?? 0;

  const handleFilterChange = (newFilters: AccidentFiltersType) => {
    setSearchParams((params) => {
      params.set('page', '1');
      if (newFilters.search) params.set('search', newFilters.search);
      else params.delete('search');
      if (newFilters.statusId !== undefined) params.set('statusId', String(newFilters.statusId));
      else params.delete('statusId');
      return params;
    });
  };

  const { mutate: removeAccident } = useMutation(useCommonApi().removeAccident());

  const handleCreateNewAccident = () => {
    if (Boolean(user?.accidentInProgress)) {
      setOpenNewAccidentDialog(true);
    } else {
      navigate(PATHS.NEW_ACCIDENT());
    }
  };

  const handleRemoveAccidentAndCreateNew = () => {
    if (user?.accidentInProgress) removeAccident({ accidentId: user?.accidentInProgress?.toString() });
    navigate(PATHS.NEW_ACCIDENT());
  };

  const handleAccidentRemove = () => {
    if (!openRemoveAccidentDialog) return;
    removeAccident(
      { accidentId: openRemoveAccidentDialog?.toString() },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            setOpenRemoveAccidentDialog(undefined);
            notifyOnSuccess('Nehoda byla smazána!');
          },
          failed: () => {
            setOpenRemoveAccidentDialog(undefined);
            notifyOnError('Nelze smazat nehodu!');
          }
        })
      }
    );
  };

  return (
    <PageLayout
      title="Řešení nehod"
      extra={
        user?.role === Role.Dealer && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlusCircle size="20px" />}
            onClick={handleCreateNewAccident}
            size="large"
          >
            Nová nehoda
          </Button>
        )
      }
    >
      <Stack spacing={2} height={1}>
        {user?.role === Role.Dealer && (
          <AccidentsFilters
            filters={{ filters, setFilters: handleFilterChange }}
            statuses={accidentsStatuses}
            disabled={isPending}
          />
        )}
        {isPending ? (
          <Stack width={1} alignItems="center">
            <CircularProgress color="primary" />
          </Stack>
        ) : accidents.length ? (
          <AccidentsList
            accidents={accidents}
            detailPath={accidentDetailPath}
            onRemove={(id) => setOpenRemoveAccidentDialog(id)}
          />
        ) : (
          <NoDataFound />
        )}
        <FlexBlock />
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={(_, p) =>
              setSearchParams((params) => {
                params.set('page', String(p));
                return params;
              })
            }
          />
        )}
      </Stack>
      <AccidentAccidentInProgressDialog
        open={openNewAccidentDialog}
        onClose={() => setOpenNewAccidentDialog(false)}
        onAccept={() => navigate(PATHS.NEW_ACCIDENT(user?.accidentInProgress?.toString()))}
        onDeny={handleRemoveAccidentAndCreateNew}
      />
      <AccidentRemoveConfirmDialog
        onAccept={handleAccidentRemove}
        open={!!openRemoveAccidentDialog}
        onDeny={() => setOpenRemoveAccidentDialog(undefined)}
        onClose={() => setOpenRemoveAccidentDialog(undefined)}
      />
    </PageLayout>
  );
};
